import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../components/app/AdminLayout";
import { adminApi, fileUrl, formatApiError } from "../../lib/api";
import { ABILITY_LABELS } from "../../lib/scoring";
import { AssignDatesModal } from "../../components/app/AssignModals";
import AvailabilityCalendar from "../../components/app/AvailabilityCalendar";
import {
  Sparkles, Search, User, Image as ImageIcon, Loader2, X, Check, Star,
  ChevronRight, ArrowDownWideNarrow, Users as UsersIcon, Mail as MailIcon,
} from "lucide-react";

const scoreColor = (s) => (s == null ? "#4a4843" : s >= 7 ? "#22C55E" : s >= 5 ? "#F59E0B" : "#EF4444");

const STAGE_META = {
  preliminary: { label: "Preliminary", color: "#F59E0B" },
  invited: { label: "Invited", color: "#3B82F6" },
  confirmed: { label: "Confirmed", color: "#22C55E" },
};
function StageTag({ stage }) {
  const m = STAGE_META[stage];
  if (!m) return null;
  return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 border" style={{ color: m.color, borderColor: `${m.color}66` }} data-testid={`rolematch-stage-${stage}`}>{stage === "confirmed" ? <Check size={12} /> : null}{m.label}</span>;
}

function Bar({ value, color }) {
  return (
    <span className="w-14 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden inline-block align-middle">
      <span className="block h-full rounded-full" style={{ width: `${value}%`, background: color }} />
    </span>
  );
}

function GenderTag({ g }) {
  if (!g || g === "either") return null;
  return <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#262626] text-[#8a857d]">{g} role</span>;
}

export default function RoleMatch() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [roleSearch, setRoleSearch] = useState("");
  const [selected, setSelected] = useState(null); // role_type_id
  const [data, setData] = useState(null);
  const [loadingCands, setLoadingCands] = useState(false);
  const [tab, setTab] = useState("system"); // system | picks
  const [sort, setSort] = useState("match"); // match | score
  const [panelId, setPanelId] = useState(null);

  // shared data for assign modal
  const [rolesFull, setRolesFull] = useState([]);
  const [showDates, setShowDates] = useState([]);
  const [assignRole, setAssignRole] = useState(null);
  const [assignActor, setAssignActor] = useState(null);

  const loadRoles = () => adminApi.get("/admin/role-match/roles").then(({ data }) => setRoles(data.items || [])).finally(() => setLoadingRoles(false));

  useEffect(() => {
    loadRoles();
    adminApi.get("/admin/roles").then(({ data }) => setRolesFull(data.items || []));
    adminApi.get("/admin/show-dates").then(({ data }) => setShowDates(data.items || []));
  }, []);

  const loadCandidates = (rtid) => {
    setSelected(rtid); setLoadingCands(true); setData(null); setPanelId(null);
    adminApi.get(`/admin/role-match/${rtid}/candidates`).then(({ data }) => setData(data)).finally(() => setLoadingCands(false));
  };

  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    return q ? roles.filter((r) => (r.name || "").toLowerCase().includes(q)) : roles;
  }, [roles, roleSearch]);

  const candidates = useMemo(() => data?.candidates || [], [data]);
  const list = useMemo(() => {
    let l = tab === "picks" ? candidates.filter((c) => c.is_preferred) : candidates.filter((c) => c.gender_ok);
    l = [...l].sort((a, b) => {
      if (sort === "score") return (b.overall_score ?? -1) - (a.overall_score ?? -1);
      return (b.suitability ?? -1) - (a.suitability ?? -1);
    });
    return l;
  }, [candidates, tab, sort]);

  const openAssign = (candidate) => {
    const role = rolesFull.find((r) => (r.role_type_id || r.id) === selected);
    if (!role) { toast.error("Role not found."); return; }
    // Use the clean role-type name (e.g. "Zombie") for display, not the first
    // position's name ("Zombie 1"), so the modal reads "to Zombie" / "Zombie #1…#5".
    setAssignRole({ ...role, name: role.base_name || data?.role?.name || role.name });
    setAssignActor(candidate);
  };
  const confirmAssign = async ({ date_ids, send_email, preliminary, role_id }) => {
    await adminApi.post(`/admin/roles/${role_id || assignRole.id}/assign-dates`, { actor_id: assignActor.id, date_ids, send_email, preliminary });
    setAssignRole(null); setAssignActor(null);
    toast.success(preliminary ? "Penciled in (preliminary)" : "Role assigned & invited");
    loadCandidates(selected); loadRoles();
  };
  const [pending, setPending] = useState(0);
  const loadPending = () => adminApi.get("/admin/role-match/pending-invites").then(({ data }) => setPending(data.count || 0));
  useEffect(() => { loadPending(); }, [roles]);
  const sendAllInvites = async () => {
    if (!window.confirm(`Send role invites to all ${pending} preliminary applicant(s)? They will each receive an email offer.`)) return;
    const { data } = await adminApi.post("/admin/role-match/send-invites");
    toast.success(`Sent ${data.invited} invite(s)`);
    loadPending(); if (selected) loadCandidates(selected); loadRoles();
  };
  const sendRoleInvites = async () => {
    const n = candidates.filter((c) => c.assignment_stage === "preliminary").length;
    if (!window.confirm(`Send invites to the ${n} preliminary applicant(s) penciled into ${data.role.name}?`)) return;
    const { res } = await adminApi.post(`/admin/role-match/${selected}/send-invites`).then((r) => ({ res: r.data }));
    toast.success(`Sent ${res.invited} invite(s)`);
    loadPending(); loadCandidates(selected); loadRoles();
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tight flex items-center gap-3"><Sparkles className="text-blood" size={30} /> Role Match</h1>
          <p className="text-sm text-[#8a857d] mt-1">Cast your roles from scored auditions — by attendee picks or the system's best match.</p>
        </div>
        {pending > 0 && (
          <button onClick={sendAllInvites} data-testid="rolematch-send-all-invites"
            className="flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
            <MailIcon size={14} /> Send all invites ({pending} preliminary)
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-5">
        {/* Roles list */}
        <div className="border border-[#1a1a1a] bg-[#0A0A0A]">
          <div className="p-3 border-b border-[#1a1a1a]">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
              <input value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)} placeholder="Search roles…" data-testid="rolematch-role-search"
                className="w-full bg-[#050505] border border-[#262626] pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-blood" />
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="rolematch-roles">
            {loadingRoles ? (
              <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-blood" size={20} /></div>
            ) : filteredRoles.length === 0 ? (
              <p className="text-sm text-[#6B6862] py-10 text-center">No roles found.</p>
            ) : filteredRoles.map((r) => (
              <button key={r.role_type_id} onClick={() => loadCandidates(r.role_type_id)} data-testid={`rolematch-role-${r.role_type_id}`}
                className={`w-full flex items-center gap-3 text-left px-3 py-2.5 border-b border-[#141414] transition-colors ${selected === r.role_type_id ? "bg-blood/10 border-l-2 border-l-blood" : "hover:bg-[#111]"}`}>
                <div className="w-10 h-10 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                  {r.profile_image ? <img src={fileUrl(r.profile_image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={15} className="text-[#3a3835]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase tracking-tight truncate leading-tight">{r.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-[#6B6862]">{r.assigned_count}/{r.total_spots} cast</span>
                    {r.fully_cast && <span className="text-[9px] uppercase tracking-wider text-[#22C55E]">Full</span>}
                    {!r.has_profile && <span className="text-[9px] uppercase tracking-wider text-[#6B6862]" title="No ideal ability profile set">no profile</span>}
                  </div>
                </div>
                <ChevronRight size={16} className="shrink-0 text-[#3a3835]" />
              </button>
            ))}
          </div>
        </div>

        {/* Candidates */}
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] min-h-[60vh]">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center py-24 text-center px-6">
              <Sparkles size={32} className="text-[#3a3835] mb-3" />
              <p className="text-sm text-[#6B6862]">Pick a role on the left to see matched applicants.</p>
            </div>
          ) : loadingCands ? (
            <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-blood" size={24} /></div>
          ) : data && (
            <div>
              {/* Role header */}
              <div className="flex items-center gap-4 p-4 border-b border-[#1a1a1a]">
                <div className="w-14 h-14 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                  {data.role.profile_image ? <img src={fileUrl(data.role.profile_image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-[#3a3835]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-2xl uppercase tracking-tight truncate">{data.role.name}</h2>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#8a857d]">
                    <span>{data.role.assigned_count}/{data.role.total_spots} cast</span>
                    {data.role.skill_level ? <span>· Skill {data.role.skill_level}</span> : null}
                    <GenderTag g={data.role.preferred_gender} />
                    {!data.role.has_profile && <span className="text-[#6B6862]">· no ability profile — ranked by score</span>}
                  </div>
                </div>
                <button onClick={() => navigate(`/admin/roles/${data.role.role_id}`)} data-testid="rolematch-edit-role"
                  className="text-[11px] uppercase tracking-wider text-[#6B6862] hover:text-blood shrink-0">Edit role</button>
                {candidates.filter((c) => c.assignment_stage === "preliminary").length > 0 && (
                  <button onClick={sendRoleInvites} data-testid="rolematch-invite-role"
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider border border-blood text-blood hover:bg-blood/10">
                    <MailIcon size={13} /> Invite penciled ({candidates.filter((c) => c.assignment_stage === "preliminary").length})
                  </button>
                )}
              </div>

              {/* Tabs + sort */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a] flex-wrap gap-3">
                <div className="flex gap-2">
                  <button onClick={() => setTab("picks")} data-testid="rolematch-tab-picks"
                    className={`px-4 py-2 text-sm uppercase tracking-wider border transition-colors ${tab === "picks" ? "border-blood bg-blood/15 text-[#F2EBE1]" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"}`}>
                    <Star size={13} className="inline mr-1.5 -mt-0.5" />Attendee Picks ({candidates.filter((c) => c.is_preferred).length})
                  </button>
                  <button onClick={() => setTab("system")} data-testid="rolematch-tab-system"
                    className={`px-4 py-2 text-sm uppercase tracking-wider border transition-colors ${tab === "system" ? "border-blood bg-blood/15 text-[#F2EBE1]" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"}`}>
                    <Sparkles size={13} className="inline mr-1.5 -mt-0.5" />System Match ({candidates.filter((c) => c.gender_ok).length})
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#8a857d]">
                  <ArrowDownWideNarrow size={14} /> Sort:
                  <button onClick={() => setSort("match")} data-testid="rolematch-sort-match" className={`px-2 py-1 ${sort === "match" ? "text-blood" : "hover:text-[#F2EBE1]"}`}>% Match</button>
                  <button onClick={() => setSort("score")} data-testid="rolematch-sort-score" className={`px-2 py-1 ${sort === "score" ? "text-blood" : "hover:text-[#F2EBE1]"}`}>Overall Score</button>
                </div>
              </div>

              {/* Candidate list */}
              <div className="divide-y divide-[#141414]" data-testid="rolematch-candidates">
                {list.length === 0 ? (
                  <p className="text-sm text-[#6B6862] py-16 text-center" data-testid="rolematch-empty">
                    {tab === "picks" ? "No attendees picked this role." : "No scored attendees match this role."}
                  </p>
                ) : list.map((c, i) => (
                  <div key={c.id} className={`flex items-center gap-3 px-4 py-3 hover:bg-[#111] cursor-pointer ${c.fully_booked && !c.already_assigned ? "opacity-60" : ""}`} onClick={() => setPanelId(c.id)} data-testid={`rolematch-candidate-${c.id}`}>
                    <span className="font-mono text-[#6B6862] text-xs w-4 shrink-0">{i + 1}</span>
                    <div className="w-11 h-11 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center rounded-full">
                      {c.headshot_file_id ? <img src={fileUrl(c.headshot_file_id, { thumb: true })} alt="" className="w-full h-full object-cover" /> : <User size={16} className="text-[#3a3835]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase tracking-tight truncate leading-tight flex items-center gap-2">
                        {c.full_name}{c.age ? <span className="text-[10px] text-[#6B6862] font-sans normal-case">age {c.age}</span> : null}
                        {c.is_preferred && tab === "system" && <Star size={11} className="text-blood" />}
                        {tab === "picks" && !c.gender_ok && <span className="text-[9px] uppercase tracking-wider text-[#F59E0B] border border-[#F59E0B]/40 px-1.5 py-0.5">gender mismatch</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        <span className="inline-flex items-center gap-1.5"><span className="text-[10px] text-[#6B6862]">Match</span>{c.suitability != null ? <><Bar value={c.suitability} color={scoreColor(c.suitability / 10)} /><span className="font-mono font-bold" style={{ color: scoreColor(c.suitability / 10) }}>{c.suitability}%</span></> : <span className="text-[#4a4843]">—</span>}</span>
                        <span className="inline-flex items-center gap-1.5"><span className="text-[10px] text-[#6B6862]">Score</span><span className="font-mono font-bold" style={{ color: scoreColor(c.overall_score) }}>{c.overall_score != null ? Number(c.overall_score).toFixed(1) : "—"}</span></span>
                      </div>
                    </div>
                    {c.already_assigned ? (
                      <div className="flex items-center gap-2 shrink-0">
                        <StageTag stage={c.assignment_stage} />
                        {c.other_role_names?.length > 0 && (
                          <span className="text-[10px] uppercase tracking-wider text-[#F59E0B] text-right" data-testid={`rolematch-otherroles-${c.id}`}>+{c.other_role_names.join(", ")}</span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openAssign(c); }} data-testid={`rolematch-reassign-${c.id}`} className="text-[10px] uppercase tracking-wider text-[#6B6862] hover:text-blood">Edit</button>
                      </div>
                    ) : c.fully_booked ? (
                      <div className="flex flex-col items-end gap-1 shrink-0 text-right" data-testid={`rolematch-fullybooked-${c.id}`}>
                        <span className="text-[10px] uppercase tracking-wider text-[#6B6862]">Fully booked{c.other_role_names?.length ? ` · ${c.other_role_names.join(", ")}` : ""}</span>
                        <button disabled data-testid={`rolematch-assign-${c.id}`}
                          className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#4a4843] cursor-not-allowed inline-flex items-center gap-1.5"><Check size={12} /> Assign</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2.5 shrink-0">
                        {c.other_role_names?.length > 0 && (
                          <span className="text-[10px] uppercase tracking-wider text-[#F59E0B] text-right leading-tight" data-testid={`rolematch-partialbooked-${c.id}`}>
                            Assigned as {c.other_role_names.join(", ")}<br /><span className="text-[#8a857d]">{c.dates_left} date{c.dates_left === 1 ? "" : "s"} left</span>
                          </span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openAssign(c); }} data-testid={`rolematch-assign-${c.id}`}
                          className="px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] inline-flex items-center gap-1.5"><Check size={12} /> Assign</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {panelId && (
        <CandidatePanel
          applicantId={panelId}
          roleName={data?.role?.name}
          showDates={showDates}
          rolesFull={rolesFull}
          onClose={() => setPanelId(null)}
          onAssign={(cand) => openAssign(cand)}
          alreadyAssigned={(candidates.find((c) => c.id === panelId) || {}).already_assigned}
          fullyBooked={(candidates.find((c) => c.id === panelId) || {}).fully_booked}
        />
      )}

      {assignRole && assignActor && (
        <AssignDatesModal
          actor={{ id: assignActor.id, full_name: assignActor.full_name, available_dates: assignActor.available_dates || [] }}
          role={assignRole}
          positions={rolesFull.filter((r) => (r.role_type_id || r.id) === selected)}
          showDates={showDates}
          takenDateIds={(assignRole.assignments || []).map((x) => x.show_date_id)}
          occupiedByOther={rolesFull.reduce((acc, r) => {
            if (r.id === assignRole.id) return acc;
            (r.assignments || []).forEach((x) => { if (x.actor_id === assignActor.id) acc[x.show_date_id] = r.name; });
            return acc;
          }, {})}
          onConfirm={confirmAssign}
          onClose={() => { setAssignRole(null); setAssignActor(null); }}
        />
      )}
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
function CandidatePanel({ applicantId, roleName, showDates, rolesFull, onClose, onAssign, alreadyAssigned, fullyBooked }) {
  const [a, setA] = useState(null);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminApi.get(`/admin/applicants/${applicantId}`).then(({ data }) => data),
      adminApi.get(`/admin/applicants/${applicantId}/scores`).then(({ data }) => data).catch(() => null),
    ]).then(([app, sc]) => { setA(app); setScores(sc); }).finally(() => setLoading(false));
  }, [applicantId]);

  const age = (() => {
    const dob = a?.date_of_birth;
    if (!dob) return null;
    try { const d = new Date(dob); const t = new Date(); let x = t.getFullYear() - d.getFullYear(); if (t < new Date(t.getFullYear(), d.getMonth(), d.getDate())) x--; return x; } catch { return null; }
  })();

  const prefNames = (a?.preferred_role_ids || []).map((rid) => {
    const r = rolesFull.find((x) => (x.role_type_id || x.id) === rid);
    return r ? (r.base_name || r.name) : "Unknown";
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="rolematch-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0A0A0A] border-l border-[#262626] h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#1a1a1a] px-4 py-3 flex items-center justify-between z-10">
          <span className="label-mini">Audition Summary</span>
          <button onClick={onClose} data-testid="rolematch-panel-close" className="text-[#6B6862] hover:text-[#F2EBE1]"><X size={18} /></button>
        </div>
        {loading || !a ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-blood" size={22} /></div>
        ) : (
          <div className="p-4 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center rounded-full">
                {a.headshot_file_id ? <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt="" className="w-full h-full object-cover" /> : <User size={22} className="text-[#3a3835]" />}
              </div>
              <div className="min-w-0">
                <button onClick={() => navigate(`/admin/applicants/${a.id}`)} className="font-display text-xl uppercase tracking-tight leading-tight hover:text-blood text-left" data-testid="rolematch-panel-name">{a.full_name}</button>
                <div className="text-xs text-[#8a857d] mt-0.5">{age ? `Age ${age}` : ""}{a.gender ? `${age ? " · " : ""}${a.gender}` : ""}</div>
              </div>
            </div>

            {scores && scores.judges_scored > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="label-mini">Scores</span>
                  <span className="font-display text-2xl text-blood leading-none">{scores.overall ?? "—"}<span className="text-xs text-[#6B6862]">/10</span></span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6862] border-b border-[#1a1a1a]">
                      <th className="py-1.5 pr-2 font-normal">Ability</th>
                      {scores.judges.map((j) => <th key={j.judge_id} className="py-1.5 px-1 font-normal text-center">{(j.judge_name || "J").split(" ")[0]}</th>)}
                      <th className="py-1.5 pl-1 font-normal text-center text-blood">Avg</th>
                    </tr></thead>
                    <tbody>
                      {(scores.abilities || []).map((ab) => (
                        <tr key={ab} className="border-b border-[#141414]">
                          <td className="py-1 pr-2 text-[#A19D94] whitespace-nowrap">{ABILITY_LABELS[ab] || ab}</td>
                          {scores.judges.map((j) => <td key={j.judge_id} className="py-1 px-1 text-center font-mono text-[#F2EBE1]">{j.scores?.[ab] ?? "—"}</td>)}
                          <td className="py-1 pl-1 text-center font-mono text-blood">{scores.per_ability?.[ab] ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {scores.judges.some((j) => (j.notes || "").trim()) && (
                  <div className="mt-3 space-y-2">
                    <span className="label-mini">Judge Notes</span>
                    {scores.judges.filter((j) => (j.notes || "").trim()).map((j) => (
                      <div key={j.judge_id} className="border border-[#1a1a1a] p-2">
                        <div className="text-[10px] uppercase tracking-wider text-[#6B6862] mb-0.5">{j.judge_name}</div>
                        <p className="text-xs text-[#A19D94] whitespace-pre-wrap">{j.notes}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : <p className="text-xs text-[#6B6862]">No scores recorded.</p>}

            {prefNames.length > 0 && (
              <div>
                <span className="label-mini block mb-2">Their Preferred Roles</span>
                <div className="flex flex-wrap gap-1.5">
                  {prefNames.map((n, i) => <span key={i} className="px-2 py-1 text-xs border border-[#262626] text-[#A19D94]">{n}</span>)}
                </div>
              </div>
            )}

            <div>
              <span className="label-mini block mb-2">Availability</span>
              <AvailabilityCalendar showDates={showDates} availableIds={a.available_dates || []} editable={false} />
            </div>

            {!alreadyAssigned && (
              fullyBooked ? (
                <div className="w-full text-center py-3 text-xs uppercase tracking-wider border border-[#262626] text-[#6B6862]" data-testid="rolematch-panel-fullybooked">
                  Fully booked — no available dates left
                </div>
              ) : (
                <button onClick={() => onAssign(a)} data-testid="rolematch-panel-assign"
                  className="w-full flex items-center justify-center gap-2 bg-blood text-white py-3 text-sm uppercase tracking-wider hover:bg-[#FF3333]">
                  <Check size={16} /> Assign to {roleName}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
